import React, { useEffect, useState, useRef, useCallback } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { CalendarService } from "../../services/CalendarService";
import { ProvidersService } from "../../services/ProvidersService";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import CropPhotoModal from "../../components/CropPhotoModal";
import { getSlotThumbnailUrl } from "../../utils/slotThumbnail";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SLOT_HEIGHT_PX = 50;
// Max provider avatars shown inline on the calendar selector; the rest go into the overflow menu.
const MAX_VISIBLE_PROVIDER_AVATARS = 6;

// API serializes DayOfWeek via JsonStringEnumConverter, so values may arrive as
// "Monday" instead of 1. Normalize to the numeric value JS Date uses (0=Sunday).
const DAY_NAME_TO_NUM = Object.fromEntries(DAY_NAMES.map((n, i) => [n, i]));
function normalizeDayOfWeek(d) {
    return typeof d === "string" ? (DAY_NAME_TO_NUM[d] ?? 0) : d;
}

function generateGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
function toTimeString(timeOnly) {
    if (!timeOnly) return "09:00";
    const h = String(timeOnly.hour).padStart(2, "0");
    const m = String(timeOnly.minute).padStart(2, "0");
    return `${h}:${m}`;
}

function fromTimeString(str) {
    const [h, m] = str.split(":").map(Number);
    return { hour: h, minute: m };
}

function getSlotInitials(name) {
    if (!name || typeof name !== 'string') return '?';
    const trimmed = name.trim();
    if (!trimmed) return '?';
    const words = trimmed.split(/\s+/);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return words[0][0].toUpperCase();
}


function buildDefaultWeek() {
    return [1, 2, 3, 4, 5, 6, 0].map((d) => ({
        dayOfWeek: d,
        startTime: { hour: 9, minute: 0 },
        endTime: { hour: 17, minute: 0 },
    }));
}

function getMonday(date) {
    const d = dayjs(date);
    const dayNum = d.day();
    const daysToMonday = dayNum === 0 ? -6 : 1 - dayNum;
    return d.add(daysToMonday, 'day').startOf('day');
}

function getMinutesFromMidnight(date) {
    const d = dayjs(date);
    return d.hour() * 60 + d.minute();
}

function getBlockSegmentsForDay(blocks, dayDate, dayOfWeek, workingHours) {
    if (!blocks || !Array.isArray(blocks)) return [];

    const dayHours = workingHours.find(h => h.dayOfWeek === dayOfWeek);
    if (!dayHours) return [];

    const workStart = (dayHours?.startTime?.hour ?? 9) * 60 + (dayHours?.startTime?.minute ?? 0);
    const workEnd = (dayHours?.endTime?.hour ?? 17) * 60 + (dayHours?.endTime?.minute ?? 0);

    const segments = [];

    blocks.forEach(block => {
        const blockStart = dayjs(block.start);
        const blockEnd = dayjs(block.end);
        const blockStartDate = blockStart.format('YYYY-MM-DD');
        const blockEndDate = blockEnd.format('YYYY-MM-DD');
        const dayDateStr = dayDate.format('YYYY-MM-DD');

        if (blockStartDate <= dayDateStr && blockEndDate >= dayDateStr) {
            let segmentStart;
            let segmentEnd;

            if (blockStartDate === dayDateStr) {
                segmentStart = getMinutesFromMidnight(blockStart);
            } else {
                segmentStart = 0;
            }

            if (blockEndDate === dayDateStr) {
                segmentEnd = getMinutesFromMidnight(blockEnd);
            } else {
                segmentEnd = 24 * 60;
            }

            const clippedStart = Math.max(segmentStart, workStart);
            const clippedEnd = Math.min(segmentEnd, workEnd);

            if (clippedStart < clippedEnd) {
                segments.push({
                    block,
                    visibleStart: clippedStart,
                    visibleEnd: clippedEnd,
                    visibleMinutes: clippedEnd - clippedStart,
                });
            }
        }
    });

    return segments;
}


function calculateAppointmentDurationMinutes(apt) {
    if (apt.start && apt.end) {
        const start = dayjs(apt.start);
        const end = dayjs(apt.end);
        return end.diff(start, 'minute');
    }
    if (apt.appointmentLength) {
        return apt.appointmentLength;
    }
    if (apt.length) {
        return apt.length;
    }
    return 30;
}

function calculateAppointmentHeight(durationMinutes) {
    const MIN_HEIGHT = 32;
    const height = (durationMinutes / 30) * SLOT_HEIGHT_PX;
    return Math.max(height, MIN_HEIGHT);
}

function HourlyScheduleGrid({ weekStartDate, appointments, blocks, workingHours, error }) {
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
        weekDays.push(weekStartDate.add(i, 'day'));
    }

    const allSlots = [];
    for (let h = 0; h < 24; h++) {
        allSlots.push({ hour: h, minute: 0 });
        allSlots.push({ hour: h, minute: 30 });
    }

    const workingHourRange = workingHours.length > 0
        ? { start: Math.min(...workingHours.map(h => h.startTime?.hour ?? 9)), end: Math.max(...workingHours.map(h => h.endTime?.hour ?? 17)) }
        : { start: 9, end: 17 };

    let appointmentHourRange = null;
    const allAppointmentMinutes = [];
    Object.values(appointments).forEach(dayAppts => {
        if (Array.isArray(dayAppts)) {
            dayAppts.forEach(apt => {
                if (apt.start) {
                    const d = dayjs(apt.start);
                    allAppointmentMinutes.push(d.hour() * 60 + d.minute());
                }
                if (apt.end) {
                    const d = dayjs(apt.end);
                    allAppointmentMinutes.push(d.hour() * 60 + d.minute());
                }
            });
        }
    });
    if (allAppointmentMinutes.length > 0) {
        const minMinutes = Math.min(...allAppointmentMinutes);
        const maxMinutes = Math.max(...allAppointmentMinutes);
        appointmentHourRange = {
            start: Math.floor(minMinutes / 60),
            end: Math.ceil(maxMinutes / 60)
        };
    }

    const visibleStart = appointmentHourRange
        ? Math.min(workingHourRange.start, appointmentHourRange.start)
        : workingHourRange.start;
    const visibleEnd = appointmentHourRange
        ? Math.max(workingHourRange.end, appointmentHourRange.end)
        : workingHourRange.end;

    const visibleSlots = allSlots.filter(s => s.hour >= visibleStart && s.hour < visibleEnd);

    if (error) {
        return (
            <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="error">
                    Failed to load appointments. Please refresh the page.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ overflowX: 'auto', mb: 3 }}>
            <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 60 }}>Time</TableCell>
                        {weekDays.map(day => (
                            <TableCell key={day.format('YYYY-MM-DD')} sx={{ fontWeight: 600, textAlign: 'center', minWidth: 120 }}>
                                <Box>{day.format('ddd')}</Box>
                                <Box sx={{ fontSize: '0.85rem', color: 'textSecondary' }}>{day.format('MMM D')}</Box>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visibleSlots.map((slot) => (
                            <TableRow key={`${slot.hour}-${slot.minute}`} sx={{ height: SLOT_HEIGHT_PX }}>
                                <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem', height: SLOT_HEIGHT_PX, py: 0, px: 1 }}>
                                    {String(slot.hour).padStart(2, '0')}:{String(slot.minute).padStart(2, '0')}
                                </TableCell>
                                {weekDays.map(day => {
                                    const dayNum = day.day();
                                    const dayHours = workingHours.find(h => h.dayOfWeek === dayNum);
                                    const slotStartMinutes = slot.hour * 60 + slot.minute;
                                    const slotEndMinutes = slotStartMinutes + 30;
                                    const startTimeMinutes = (dayHours?.startTime?.hour ?? 9) * 60 + (dayHours?.startTime?.minute ?? 0);
                                    const endTimeMinutes = (dayHours?.endTime?.hour ?? 17) * 60 + (dayHours?.endTime?.minute ?? 0);
                                    const isWorkingHour = dayHours &&
                                        startTimeMinutes < slotEndMinutes &&
                                        endTimeMinutes > slotStartMinutes;

                                    const dateStr = day.format('YYYY-MM-DD');
                                    const dayAppts = appointments[dateStr] || [];
                                    const aptsInSlot = dayAppts.filter(apt => {
                                        const aptMinutes = dayjs(apt.start).hour() * 60 + dayjs(apt.start).minute();
                                        return aptMinutes >= slotStartMinutes && aptMinutes < slotEndMinutes;
                                    });

                                    const daySegments = getBlockSegmentsForDay(blocks, day, dayNum, workingHours);
                                    const segmentsInSlot = daySegments.filter(segment =>
                                        segment.visibleStart >= slotStartMinutes && segment.visibleStart < slotEndMinutes
                                    );

                                    return (
                                        <TableCell
                                            key={`${dateStr}-${slot.hour}-${slot.minute}`}
                                            sx={{
                                                bgcolor: isWorkingHour ? '#e6f4ea' : '#fafafa',
                                                borderLeft: !isWorkingHour && (slot.hour > 0 || slot.minute > 0) ? '2px solid #e0e0e0' : undefined,
                                                py: 0,
                                                px: 1,
                                                height: SLOT_HEIGHT_PX,
                                                position: 'relative',
                                                verticalAlign: 'top',
                                                overflow: 'visible',
                                            }}
                                        >
                                            {segmentsInSlot.length > 0 && (
                                                <>
                                                    {segmentsInSlot.map((segment, bIdx) => {
                                                        const block = segment.block;
                                                        const blockStart = dayjs(block.start);
                                                        const offsetMinutes = segment.visibleStart - slotStartMinutes;
                                                        const offsetPx = (offsetMinutes / 30) * SLOT_HEIGHT_PX;
                                                        const heightPx = (segment.visibleMinutes / 30) * SLOT_HEIGHT_PX;
                                                        return (
                                                            <Box
                                                                key={`block-${block.id || bIdx}`}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: offsetPx,
                                                                    left: '4px',
                                                                    right: '4px',
                                                                    height: heightPx,
                                                                    p: 0.5,
                                                                    bgcolor: '#ff9800',
                                                                    color: '#fff',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.7rem',
                                                                    zIndex: 1,
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                <Box sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                                                    {block.title || 'Block'}
                                                                </Box>
                                                                <Box sx={{ fontSize: '0.6rem', mt: 0.25 }}>
                                                                    {blockStart.format('HH:mm')} - {dayjs(block.end).format('HH:mm')}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                </>
                                            )}
                                            {aptsInSlot.length > 0 && (
                                                <>
                                                    {aptsInSlot.map((apt, aIdx) => {
                                                        const clientDisplay = apt.clientName
                                                            ? apt.clientName
                                                            : (apt.clientId ? `Client: ${apt.clientId}` : 'Client: Unknown');
                                                        const bgColor = apt.isPaid ? '#4caf50' : '#1976d2';
                                                        const borderColor = apt.isPaid ? '#388e3c' : '#1565c0';
                                                        const durationMinutes = calculateAppointmentDurationMinutes(apt);
                                                        const appointmentHeight = calculateAppointmentHeight(durationMinutes);
                                                        const aptStartMinutes = dayjs(apt.start).hour() * 60 + dayjs(apt.start).minute();
                                                        const slotOffsetMinutes = Math.max(0, aptStartMinutes - slotStartMinutes);
                                                        const slotOffsetPx = (slotOffsetMinutes / 30) * SLOT_HEIGHT_PX;
                                                        const stackOffset = aIdx * 4;
                                                        const topPx = slotOffsetPx + stackOffset;
                                                        const tooltipEnd = apt.end ? dayjs(apt.end) : dayjs(apt.start).add(durationMinutes, 'minute');
                                                        const tooltipTitle = (
                                                            <Box sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                                                                <Box sx={{ fontWeight: 600 }}>
                                                                    {dayjs(apt.start).format('HH:mm')} - {tooltipEnd.format('HH:mm')}
                                                                </Box>
                                                                {apt.service && <Box>{apt.service}</Box>}
                                                                <Box>{clientDisplay}</Box>
                                                                {apt.clientPhone && <Box>Phone: {apt.clientPhone}</Box>}
                                                                <Box>{apt.isPaid ? 'Paid' : 'Unpaid'}</Box>
                                                            </Box>
                                                        );
                                                        return (
                                                            <Tooltip key={aIdx} title={tooltipTitle} placement="top">
                                                                <Box
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: topPx,
                                                                        left: '4px',
                                                                        right: '4px',
                                                                        height: appointmentHeight,
                                                                        p: 0.5,
                                                                        bgcolor: bgColor,
                                                                        color: '#fff',
                                                                        borderRadius: '4px',
                                                                        fontSize: '0.7rem',
                                                                        border: `2px solid ${borderColor}`,
                                                                        overflow: 'hidden',
                                                                        zIndex: 2 + aIdx,
                                                                        cursor: 'help',
                                                                    }}
                                                                >
                                                                    <Box sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                                                        {dayjs(apt.start).format('HH:mm')}
                                                                        {apt.isPaid && (
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'inline-block',
                                                                                    ml: 0.5,
                                                                                    px: 0.5,
                                                                                    bgcolor: 'rgba(255,255,255,0.3)',
                                                                                    borderRadius: '2px',
                                                                                    fontSize: '0.55rem',
                                                                                    fontWeight: 500,
                                                                                }}
                                                                            >
                                                                                PAID
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                    {apt.service && (
                                                                        <Box sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                                                                            {apt.service}
                                                                        </Box>
                                                                    )}
                                                                    <Box sx={{ fontSize: '0.6rem', mt: 0.25, opacity: 0.9 }}>
                                                                        {clientDisplay}
                                                                    </Box>
                                                                    {apt.clientPhone && (
                                                                        <Box sx={{ fontSize: '0.6rem', mt: 0.25, opacity: 0.8 }}>
                                                                            {apt.clientPhone}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        )
                    )}
                </TableBody>
            </Table>
        </Box>
    );
}

function AppointmentsView({ providerId, weekStartDate, workingHours, loading, onRefreshBlocks, onBlocksUpdate, selectedSlotId, refreshToken }) {
    const [appointments, setAppointments] = useState({});
    const [blocks, setBlocks] = useState([]);
    const [appointmentLoading, setAppointmentLoading] = useState(false);
    const [appointmentError, setAppointmentError] = useState(false);
    const isFetchingRef = useRef(false);

    const fetchAppointmentsAndBlocks = useCallback(async () => {
        if (!providerId || !weekStartDate || isFetchingRef.current) return;

        isFetchingRef.current = true;
        setAppointmentError(false);

        try {
            const schedule = await CalendarService.getAppointments(providerId, selectedSlotId || null);
            const appts = {};

            if (schedule && schedule.appointments && Array.isArray(schedule.appointments)) {
                schedule.appointments.forEach((apt) => {
                    const aptDate = dayjs(apt.start).format('YYYY-MM-DD');
                    if (!appts[aptDate]) {
                        appts[aptDate] = [];
                    }
                    appts[aptDate].push(apt);
                });
            }

            setAppointments(appts);

            try {
                const blockData = await CalendarService.getBlocks(providerId);
                setBlocks(blockData && Array.isArray(blockData) ? blockData : []);
            } catch {
                setBlocks([]);
            }
        } catch {
            setAppointmentError(true);
            setAppointments({});
            setBlocks([]);
        } finally {
            isFetchingRef.current = false;
        }
    }, [providerId, weekStartDate, selectedSlotId]);

    const refreshBlocks = useCallback(async () => {
        if (!providerId) return;
        try {
            const blockData = await CalendarService.getBlocks(providerId);
            setBlocks(blockData && Array.isArray(blockData) ? blockData : []);
        } catch {
            setBlocks([]);
        }
    }, [providerId]);

    useEffect(() => {
        if (!providerId || !weekStartDate) return;

        setAppointmentLoading(true);
        fetchAppointmentsAndBlocks().then(() => setAppointmentLoading(false));

        const pollInterval = setInterval(() => {
            fetchAppointmentsAndBlocks();
        }, 10000);

        return () => clearInterval(pollInterval);
    }, [providerId, weekStartDate, selectedSlotId, refreshToken, fetchAppointmentsAndBlocks]);

    useEffect(() => {
        if (onRefreshBlocks) {
            onRefreshBlocks(refreshBlocks);
        }
    }, [refreshBlocks, onRefreshBlocks]);

    useEffect(() => {
        if (onBlocksUpdate) {
            onBlocksUpdate(blocks);
        }
    }, [blocks, onBlocksUpdate]);

    if (loading || appointmentLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading appointments…</Typography>
            </Box>
        );
    }

    return (
        <HourlyScheduleGrid
            weekStartDate={weekStartDate}
            appointments={appointments}
            blocks={blocks}
            workingHours={workingHours}
            error={appointmentError}
        />
    );
}

export function CalendarTab({ provider, onProviderUpdate }) {
    const providerId = provider?.id ?? null;

    const [localSlots, setLocalSlots] = useState(provider?.schedulingSlots ?? []);
    const [selectedSlotId, setSelectedSlotId] = useState(localSlots[0]?.id ?? "");
    const [overflowAnchorEl, setOverflowAnchorEl] = useState(null);

    // Sync local slots when provider changes and update selectedSlotId validity
    useEffect(() => {
        const currentSlots = provider?.schedulingSlots ?? [];
        setLocalSlots(currentSlots);
        if (currentSlots.length === 0) {
            setSelectedSlotId("");
        } else if (!currentSlots.some((s) => s.id === selectedSlotId)) {
            setSelectedSlotId(currentSlots[0].id);
        }
    }, [provider?.id, provider?.schedulingSlots]); // eslint-disable-line react-hooks/exhaustive-deps

    const [weekDays, setWeekDays] = useState(buildDefaultWeek());
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [weekStartDate, setWeekStartDate] = useState(() => getMonday(dayjs()));
    const [openNewSlotDialog, setOpenNewSlotDialog] = useState(false);
    const [newSlotName, setNewSlotName] = useState("");
    const [creatingSlot, setCreatingSlot] = useState(false);
    const [openRenameDialog, setOpenRenameDialog] = useState(false);
    const [renameSlotName, setRenameSlotName] = useState("");
    const [renamingSlot, setRenamingSlot] = useState(false);
    const [openBlockDialog, setOpenBlockDialog] = useState(false);
    const [blockData, setBlockData] = useState({ title: "", start: "", end: "" });
    const [creatingBlock, setCreatingBlock] = useState(false);
    const [refreshBlocksCallback, setRefreshBlocksCallback] = useState(null);
    const [deletingBlockId, setDeletingBlockId] = useState(null);
    const [displayedBlocks, setDisplayedBlocks] = useState([]);
    const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
    const [futureAppointments, setFutureAppointments] = useState([]);
    const [appointmentMoves, setAppointmentMoves] = useState({});
    const [deactivatingSlot, setDeactivatingSlot] = useState(false);
    const [manualRescheduleQueue, setManualRescheduleQueue] = useState([]);
    const [appointmentsRefreshToken, setAppointmentsRefreshToken] = useState(0);
    const [openRescheduleDialog, setOpenRescheduleDialog] = useState(false);
    const [selectedQueueItem, setSelectedQueueItem] = useState(null);
    const [rescheduleForm, setRescheduleForm] = useState({ newSlotId: "", newStart: "" });
    const [reschedulingAppointment, setReschedulingAppointment] = useState(false);
    const [rescheduleError, setRescheduleError] = useState("");
    const [rescheduleDate, setRescheduleDate] = useState(null);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [availableTimesLoading, setAvailableTimesLoading] = useState(false);
    const [reactivatingSlot, setReactivatingSlot] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
    const [photoModalImageSrc, setPhotoModalImageSrc] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [photoError, setPhotoError] = useState(null);
    const photoInputRef = useRef(null);

    useEffect(() => {
        if (!providerId) return;
        setLoading(true);
        setSaveStatus(null);
        CalendarService.getWorkingHours(providerId, selectedSlotId || null)
            .then((week) => {
                if (Array.isArray(week) && week.length > 0) {
                    const normalized = week.map((d) => ({
                        ...d,
                        dayOfWeek: normalizeDayOfWeek(d.dayOfWeek),
                    }));
                    const order = [1, 2, 3, 4, 5, 6, 0];
                    const sorted = [...normalized].sort(
                        (a, b) => order.indexOf(a.dayOfWeek) - order.indexOf(b.dayOfWeek)
                    );
                    setWeekDays(sorted);
                } else {
                    setWeekDays(buildDefaultWeek());
                }
            })
            .catch(() => {
                setWeekDays(buildDefaultWeek());
            })
            .finally(() => {
                setLoading(false);
            });
    }, [providerId, selectedSlotId]);

    const handleTimeChange = (dayOfWeek, field, value) => {
        setWeekDays((prev) =>
            prev.map((d) =>
                d.dayOfWeek === dayOfWeek ? { ...d, [field]: fromTimeString(value) } : d
            )
        );
        setSaveStatus(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveStatus(null);
        try {
            await CalendarService.setWorkingHours(providerId, weekDays, selectedSlotId || null);
            setSaveStatus("saved");
        } catch {
            setSaveStatus("error");
        } finally {
            setSaving(false);
        }
    };

    const handleCreateSlot = async () => {
        if (!newSlotName.trim() || !provider) return;
        setCreatingSlot(true);
        try {
            const newSlot = {
                id: generateGuid(),
                name: newSlotName
            };
            const updatedSlots = [...localSlots, newSlot];
            const updatedProvider = {
                ...provider,
                schedulingSlots: updatedSlots
            };
            const response = await ProvidersService.updateProvider(updatedProvider);
            const savedProvider = response?.data || updatedProvider;
            setLocalSlots(savedProvider.schedulingSlots || updatedSlots);
            setSelectedSlotId(newSlot.id);
            setNewSlotName("");
            setOpenNewSlotDialog(false);
            if (onProviderUpdate) {
                onProviderUpdate(savedProvider);
            }
        } catch (error) {
            console.error("Failed to create slot:", error);
        } finally {
            setCreatingSlot(false);
        }
    };

    const handleRenameSlot = async () => {
        if (!renameSlotName.trim() || !provider || !selectedSlotId) return;
        setRenamingSlot(true);
        try {
            const updatedSlots = localSlots.map(slot =>
                slot.id === selectedSlotId
                    ? { ...slot, name: renameSlotName.trim() }
                    : slot
            );
            const updatedProvider = {
                ...provider,
                schedulingSlots: updatedSlots
            };
            const response = await ProvidersService.updateProvider(updatedProvider);
            const savedProvider = response?.data || updatedProvider;
            setLocalSlots(savedProvider.schedulingSlots || updatedSlots);
            setRenameSlotName("");
            setOpenRenameDialog(false);
            if (onProviderUpdate) {
                onProviderUpdate(savedProvider);
            }
        } catch (error) {
            console.error("Failed to rename slot:", error);
        } finally {
            setRenamingSlot(false);
        }
    };

    const handleCreateBlock = async () => {
        if (!blockData.title.trim() || !blockData.start || !blockData.end || !providerId) return;
        setCreatingBlock(true);
        try {
            await CalendarService.createBlock(providerId, blockData.title, blockData.start, blockData.end);
            setBlockData({ title: "", start: "", end: "" });
            setOpenBlockDialog(false);
            if (refreshBlocksCallback) {
                await refreshBlocksCallback();
            }
        } catch (error) {
            console.error("Failed to create block:", error);
        } finally {
            setCreatingBlock(false);
        }
    };

    const handleDeleteBlock = async (blockId) => {
        if (!providerId) return;
        if (!window.confirm("Are you sure you want to delete this PTO/Holiday block?")) return;
        setDeletingBlockId(blockId);
        try {
            await CalendarService.deleteBlock(providerId, blockId);
            if (refreshBlocksCallback) {
                await refreshBlocksCallback();
            }
        } catch (error) {
            console.error("Failed to delete block:", error);
        } finally {
            setDeletingBlockId(null);
        }
    };

    const handleDeactivateSlot = async () => {
        if (!providerId || !selectedSlotId) return;

        setDeactivatingSlot(true);
        try {
            const appointments = await CalendarService.getFutureAppointmentsForSlot(providerId, selectedSlotId);
            if (appointments && appointments.length > 0) {
                const appointmentsWithValidSlots = await Promise.all(
                    appointments.map(async (apt) => {
                        try {
                            const validSlots = await CalendarService.getValidDestinationSlots(providerId, apt.id);
                            return { ...apt, validDestinationSlotIds: validSlots || [] };
                        } catch {
                            return { ...apt, validDestinationSlotIds: [] };
                        }
                    })
                );
                setFutureAppointments(appointmentsWithValidSlots);
            } else {
                setFutureAppointments([]);
            }
            setAppointmentMoves({});
            setOpenDeactivateDialog(true);
        } catch (error) {
            console.error("Failed to get future appointments:", error);
            alert("Failed to load appointments for this slot");
        } finally {
            setDeactivatingSlot(false);
        }
    };

    const handleReactivateSlot = async () => {
        if (!provider || !selectedSlotId) return;

        setReactivatingSlot(true);
        try {
            const updatedSlots = localSlots.map(slot =>
                slot.id === selectedSlotId
                    ? { ...slot, status: 1 }
                    : slot
            );
            const updatedProvider = { ...provider, schedulingSlots: updatedSlots };
            const response = await ProvidersService.updateProvider(updatedProvider);

            if (!response || !response.status || response.status < 200 || response.status >= 300) {
                throw new Error("Failed to reactivate slot: server returned an error");
            }

            const savedProvider = response.data || updatedProvider;
            setLocalSlots(savedProvider.schedulingSlots || updatedSlots);

            if (onProviderUpdate) {
                onProviderUpdate(savedProvider);
            }

            setAppointmentsRefreshToken((value) => value + 1);
        } catch (error) {
            console.error("Failed to reactivate slot:", error);
            alert("Error reactivating service provider. Please try again.");
        } finally {
            setReactivatingSlot(false);
        }
    };

    const handleConfirmDeactivation = async () => {
        if (!provider || !selectedSlotId) return;

        setDeactivatingSlot(true);
        try {
            const updatedSlots = localSlots.map(slot =>
                slot.id === selectedSlotId
                    ? { ...slot, status: 0 }
                    : slot
            );
            const updatedProvider = { ...provider, schedulingSlots: updatedSlots };
            const response = await ProvidersService.updateProvider(updatedProvider);
            const savedProvider = response?.data || updatedProvider;
            setLocalSlots(savedProvider.schedulingSlots || updatedSlots);

            if (onProviderUpdate) {
                onProviderUpdate(savedProvider);
            }

            setOpenDeactivateDialog(false);

            for (const apt of futureAppointments) {
                const targetSlotId = appointmentMoves[apt.id];

                if (targetSlotId && targetSlotId !== selectedSlotId) {
                    try {
                        await CalendarService.moveAppointmentToSlot(providerId, apt.id, targetSlotId);
                    } catch (error) {
                        console.error(`Failed to move appointment ${apt.id}:`, error);
                        await CalendarService.createManualRescheduleQueueItem(providerId, {
                            appointmentId: apt.id,
                            originalSlotId: selectedSlotId,
                            clientName: apt.clientName || "Unknown",
                            clientPhone: apt.clientPhone || "",
                            clientEmail: apt.clientEmail || "",
                            appointmentStart: apt.start,
                            appointmentLength: apt.length,
                            reason: "Failed to move automatically - slot deactivated"
                        });
                    }
                } else {
                    await CalendarService.createManualRescheduleQueueItem(providerId, {
                        appointmentId: apt.id,
                        originalSlotId: selectedSlotId,
                        clientName: apt.clientName || "Unknown",
                        clientPhone: apt.clientPhone || "",
                        clientEmail: apt.clientEmail || "",
                        appointmentStart: apt.start,
                        appointmentLength: apt.length,
                        reason: "No destination service provider selected"
                    });
                }
            }

            setFutureAppointments([]);
            setAppointmentMoves({});

            await loadManualRescheduleQueue();
            setAppointmentsRefreshToken((value) => value + 1);
        } catch (error) {
            console.error("Failed to deactivate slot:", error);
            alert("Error during service provider deactivation. Some operations may have partially completed.");
        } finally {
            setDeactivatingSlot(false);
        }
    };

    const loadManualRescheduleQueue = useCallback(async () => {
        if (!providerId) return;
        try {
            const items = await CalendarService.getManualRescheduleQueueItems(providerId);
            const sortedItems = [...(items || [])].sort((a, b) => {
                if ((a.status || "") === (b.status || "")) {
                    return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
                }
                return (a.status === "pending" ? -1 : 1);
            });
            setManualRescheduleQueue(sortedItems);
        } catch (error) {
            console.error("Failed to load manual reschedule queue:", error);
        }
    }, [providerId]);

    const handlePhotoFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setPhotoModalImageSrc(reader.result);
            setShowPhotoModal(true);
        };
    };

    const handlePhotoCropComplete = async (croppedImage) => {
        if (!providerId || !selectedSlotId || !croppedImage) {
            setPhotoError("Missing required data for upload");
            return;
        }

        setUploadingPhoto(true);
        setPhotoError(null);
        try {
            // Step 1: Generate upload URL
            const uploadUrlResponse = await ProvidersService.generateSlotThumbnailUploadURL(providerId, selectedSlotId);
            if (!uploadUrlResponse || !uploadUrlResponse.data) {
                throw new Error("Failed to generate upload URL");
            }

            const { uploadUrl, thumbnailGuid } = uploadUrlResponse.data;
            if (!uploadUrl || !thumbnailGuid) {
                throw new Error("Invalid upload URL or thumbnail GUID from server");
            }

            // Step 2: Upload blob to storage
            const uploadResponse = await ProvidersService.uploadPhotoToStorage(uploadUrl, croppedImage);
            if (!uploadResponse || uploadResponse.status < 200 || uploadResponse.status >= 300) {
                throw new Error("Failed to upload image to storage");
            }

            // Step 3: Complete upload and get updated provider
            const completeResponse = await ProvidersService.completeSlotThumbnailUpload(providerId, selectedSlotId, thumbnailGuid);
            if (!completeResponse || !completeResponse.data) {
                throw new Error("Failed to complete upload");
            }

            // Update local state with new provider data
            const updatedProvider = completeResponse.data;
            if (updatedProvider.schedulingSlots) {
                setLocalSlots(updatedProvider.schedulingSlots);
            }
            if (onProviderUpdate) {
                onProviderUpdate(updatedProvider);
            }
        } catch (error) {
            console.error("Photo upload error:", error);
            setPhotoError(error?.response?.data?.title || error?.message || "Failed to upload photo");
        } finally {
            setUploadingPhoto(false);
            setShowPhotoModal(false);
            setPhotoModalImageSrc(null);
            if (photoInputRef.current) {
                photoInputRef.current.value = "";
            }
        }
    };

    useEffect(() => {
        loadManualRescheduleQueue();
    }, [loadManualRescheduleQueue]);

    useEffect(() => {
        if (!openRescheduleDialog || !rescheduleForm.newSlotId || !rescheduleDate || !selectedQueueItem || !providerId) {
            setAvailableTimes([]);
            return;
        }
        let cancelled = false;
        setAvailableTimesLoading(true);
        setAvailableTimes([]);
        const year = rescheduleDate.year();
        const month = rescheduleDate.month() + 1;
        const day = rescheduleDate.date();
        const length = selectedQueueItem.appointmentLength;
        CalendarService.getAvailableAppointments(providerId, year, month, day, length, rescheduleForm.newSlotId)
            .then((slots) => { if (!cancelled) setAvailableTimes(slots || []); })
            .catch(() => { if (!cancelled) setAvailableTimes([]); })
            .finally(() => { if (!cancelled) setAvailableTimesLoading(false); });
        return () => { cancelled = true; };
    }, [openRescheduleDialog, rescheduleForm.newSlotId, rescheduleDate, selectedQueueItem, providerId]);

    const formatQueueDateTime = (value) => {
        if (!value) return "Unknown";
        return dayjs(value).format('MMM D, YYYY HH:mm');
    };

    const getActiveSlots = useCallback(() => (
        localSlots.filter((slot) => slot.status !== 0)
    ), [localSlots]);

    const handleOpenRescheduleDialog = (item) => {
        const activeSlots = getActiveSlots();
        const defaultSlotId = activeSlots.find((slot) => slot.id !== item.originalSlotId)?.id || activeSlots[0]?.id || "";
        setSelectedQueueItem(item);
        setRescheduleForm({ newSlotId: defaultSlotId, newStart: "" });
        setRescheduleDate(item.appointmentStart ? dayjs(item.appointmentStart) : null);
        setAvailableTimes([]);
        setRescheduleError("");
        setOpenRescheduleDialog(true);
    };

    const handleCloseRescheduleDialog = () => {
        if (reschedulingAppointment) return;
        setOpenRescheduleDialog(false);
        setSelectedQueueItem(null);
        setRescheduleForm({ newSlotId: "", newStart: "" });
        setRescheduleDate(null);
        setAvailableTimes([]);
        setRescheduleError("");
    };

    const handleSubmitReschedule = async () => {
        if (!providerId || !selectedQueueItem) return;
        if (!rescheduleForm.newSlotId || !rescheduleDate || !rescheduleForm.newStart) {
            setRescheduleError("Select an active service provider, a date, and a time.");
            return;
        }

        setReschedulingAppointment(true);
        setRescheduleError("");
        try {
            await CalendarService.rescheduleAppointment(
                providerId,
                selectedQueueItem.appointmentId,
                rescheduleForm.newSlotId,
                rescheduleForm.newStart
            );
            await loadManualRescheduleQueue();
            setAppointmentsRefreshToken((value) => value + 1);
            setOpenRescheduleDialog(false);
            setSelectedQueueItem(null);
            setRescheduleForm({ newSlotId: "", newStart: "" });
        } catch (error) {
            const message =
                error?.response?.data?.title ||
                error?.response?.data?.errors?.OutsideWorkingHours?.[0] ||
                error?.response?.data?.errors?.ConflictingBooking?.[0] ||
                error?.response?.data?.errors?.ConflictingPtoBlock?.[0] ||
                error?.response?.data?.errors?.SlotInactive?.[0] ||
                error?.response?.data?.errors?.NotWorkingDay?.[0] ||
                error?.response?.data?.errors?.NoWorkingHours?.[0] ||
                "Unable to reschedule this appointment.";
            setRescheduleError(message);
        } finally {
            setReschedulingAppointment(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ p: '5px', pb: '25px' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    Schedule & Appointments
                </Typography>

                {localSlots.length === 0 ? (
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            You don't have any service providers yet.
                        </Typography>
                        <Button variant="contained" size="small" onClick={() => setOpenNewSlotDialog(true)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            Add Your First Service Provider
                        </Button>
                    </Box>
                ) : (() => {
                    const selectedSlot = localSlots.find(s => s.id === selectedSlotId);
                    const overflowOpen = Boolean(overflowAnchorEl);

                    // Sort deactivated providers to the end, preserving order within each group
                    const orderedSlots = [...localSlots].sort((a, b) => (a.status === 0 ? 1 : 0) - (b.status === 0 ? 1 : 0));

                    // Determine visible and overflow slots
                    let visibleSlots = orderedSlots;
                    let overflowSlots = [];
                    if (orderedSlots.length > MAX_VISIBLE_PROVIDER_AVATARS) {
                        visibleSlots = orderedSlots.slice(0, MAX_VISIBLE_PROVIDER_AVATARS);
                        overflowSlots = orderedSlots.slice(MAX_VISIBLE_PROVIDER_AVATARS);
                        // If selected slot is in overflow, promote it to visible
                        if (selectedSlot && !visibleSlots.some(s => s.id === selectedSlotId)) {
                            visibleSlots = [selectedSlot, ...visibleSlots.slice(0, -1)];
                            overflowSlots = orderedSlots.filter(s => !visibleSlots.some(v => v.id === s.id));
                        }
                    }

                    return (
                        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Row 1: Avatar selector */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {visibleSlots.map((slot) => {
                                    const isSelected = slot.id === selectedSlotId;
                                    const isDeactivated = slot.status === 0;
                                    const initials = getSlotInitials(slot.name);
                                    const tooltipTitle = slot.name ?? slot.id;
                                    const tooltipText = isDeactivated ? `${tooltipTitle} (Inactive)` : tooltipTitle;
                                    return (
                                        <Tooltip key={slot.id} title={tooltipText}>
                                            <IconButton
                                                onClick={() => setSelectedSlotId(slot.id)}
                                                sx={{
                                                    p: 0.5,
                                                    '&:hover': { transform: 'scale(1.05)' }
                                                }}
                                            >
                                                <Avatar
                                                    src={getSlotThumbnailUrl(slot, providerId) || undefined}
                                                    alt={slot.name ?? slot.id}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: isDeactivated ? 'transparent' : (isSelected ? 'primary.main' : 'grey.400'),
                                                        border: isDeactivated
                                                            ? (isSelected ? '3px solid' : '2px solid')
                                                            : (isSelected ? '2px solid' : '2px solid transparent'),
                                                        borderColor: isDeactivated
                                                            ? (isSelected ? 'primary.main' : 'grey.500')
                                                            : (isSelected ? 'primary.dark' : 'transparent'),
                                                        color: isDeactivated ? 'text.disabled' : undefined,
                                                        opacity: 1,
                                                        cursor: 'pointer',
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {initials}
                                                </Avatar>
                                            </IconButton>
                                        </Tooltip>
                                    );
                                })}

                                {/* Overflow menu button */}
                                {overflowSlots.length > 0 && (
                                    <>
                                        <Tooltip title={`${overflowSlots.length} more providers`}>
                                            <IconButton
                                                onClick={(e) => setOverflowAnchorEl(e.currentTarget)}
                                                sx={{ p: 0.5, '&:hover': { transform: 'scale(1.05)' } }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: 'grey.700',
                                                        border: '2px solid transparent',
                                                        color: '#fff',
                                                        cursor: 'pointer',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    +{overflowSlots.length}
                                                </Avatar>
                                            </IconButton>
                                        </Tooltip>
                                        <Menu
                                            anchorEl={overflowAnchorEl}
                                            open={overflowOpen}
                                            onClose={() => setOverflowAnchorEl(null)}
                                        >
                                            {overflowSlots.map((slot) => (
                                                <MenuItem
                                                    key={slot.id}
                                                    onClick={() => {
                                                        setSelectedSlotId(slot.id);
                                                        setOverflowAnchorEl(null);
                                                    }}
                                                >
                                                    {slot.name ?? slot.id}
                                                    {slot.status === 0 && " (Inactive)"}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                )}

                                {/* Add new provider button */}
                                <Tooltip title="Add new service provider">
                                    <IconButton
                                        onClick={() => setOpenNewSlotDialog(true)}
                                        sx={{
                                            p: 0.5,
                                            '&:hover': { transform: 'scale(1.05)' }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                bgcolor: 'transparent',
                                                border: '2px dashed',
                                                borderColor: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <PersonAddIcon sx={{ color: 'primary.main', fontSize: '1.25rem' }} />
                                        </Avatar>
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Row 2: Name and action buttons */}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Typography variant="subtitle1">
                                    {selectedSlot?.name ?? ""}
                                    {selectedSlot?.status === 0 && " (Inactive)"}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        setRenameSlotName(selectedSlot?.name ?? "");
                                        setOpenRenameDialog(true);
                                    }}
                                    disabled={!selectedSlotId}
                                >
                                    Rename
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => photoInputRef.current?.click()}
                                    disabled={!selectedSlotId || uploadingPhoto}
                                >
                                    {uploadingPhoto ? "Uploading…" : selectedSlot?.thumbnailGuid && selectedSlot.thumbnailGuid !== '00000000-0000-0000-0000-000000000000' ? "Change Photo" : "Set Photo"}
                                </Button>
                                {selectedSlot?.status === 0 ? (
                                    <Tooltip title="Reactivate this service provider to enable new bookings">
                                        <span>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="success"
                                                onClick={handleReactivateSlot}
                                                disabled={!selectedSlotId || reactivatingSlot}
                                            >
                                                {reactivatingSlot ? "Reactivating…" : "Reactivate"}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="">
                                        <span>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                color="warning"
                                                onClick={handleDeactivateSlot}
                                                disabled={!selectedSlotId || deactivatingSlot}
                                            >
                                                {deactivatingSlot ? "Deactivating…" : "Deactivate"}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    );
                })()}

                {photoError && (
                    <Alert severity="error" onClose={() => setPhotoError(null)} sx={{ mt: 2, mb: 2 }}>
                        {photoError}
                    </Alert>
                )}

                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2">Select week to view appointments</Typography>
                    <DatePicker
                        value={weekStartDate}
                        onChange={(newDate) => {
                            if (newDate) {
                                setWeekStartDate(getMonday(newDate));
                            }
                        }}
                        slotProps={{
                            textField: { size: 'small', sx: { maxWidth: 240 } }
                        }}
                    />
                </Box>

                <AppointmentsView
                    providerId={providerId}
                    weekStartDate={weekStartDate}
                    workingHours={weekDays}
                    loading={loading}
                    onRefreshBlocks={setRefreshBlocksCallback}
                    onBlocksUpdate={setDisplayedBlocks}
                    selectedSlotId={selectedSlotId}
                    refreshToken={appointmentsRefreshToken}
                />

                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    Working Hours
                </Typography>

                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading…</Typography>
                    </Box>
                ) : (
                    <Table size="small" sx={{ maxWidth: 520 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Day</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {weekDays.map((d) => (
                                <TableRow key={d.dayOfWeek}>
                                    <TableCell>{DAY_NAMES[d.dayOfWeek]}</TableCell>
                                    <TableCell>
                                        <input
                                            type="time"
                                            value={toTimeString(d.startTime)}
                                            onChange={(e) =>
                                                handleTimeChange(d.dayOfWeek, "startTime", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <input
                                            type="time"
                                            value={toTimeString(d.endTime)}
                                            onChange={(e) =>
                                                handleTimeChange(d.dayOfWeek, "endTime", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                    <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                    <Button variant="outlined" onClick={() => setOpenBlockDialog(true)}>
                        Add PTO/Holiday
                    </Button>
                    {saveStatus === "saved" && (
                        <Typography variant="body2" color="success.main">
                            Saved successfully.
                        </Typography>
                    )}
                    {saveStatus === "error" && (
                        <Typography variant="body2" color="error">
                            Save failed. Please try again.
                        </Typography>
                    )}
                </Box>

                {displayedBlocks.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            PTO/Holiday Blocks
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {displayedBlocks.map((block) => (
                                <Box
                                    key={block.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 1.5,
                                        bgcolor: '#fff3e0',
                                        border: '1px solid #ffe0b2',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {block.title}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {dayjs(block.start).format('MMM D, YYYY HH:mm')} - {dayjs(block.end).format('MMM D, YYYY HH:mm')}
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleDeleteBlock(block.id)}
                                        disabled={deletingBlockId === block.id}
                                    >
                                        {deletingBlockId === block.id ? "Deleting…" : "Delete"}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Dialog open={openNewSlotDialog} onClose={() => setOpenNewSlotDialog(false)}>
                    <DialogTitle>Create New Service Provider</DialogTitle>
                    <DialogContent sx={{ minWidth: 350, pt: 2 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Service provider name"
                            value={newSlotName}
                            onChange={(e) => setNewSlotName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && newSlotName.trim()) {
                                    handleCreateSlot();
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenNewSlotDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleCreateSlot}
                            disabled={creatingSlot || !newSlotName.trim()}
                            variant="contained"
                        >
                            {creatingSlot ? "Creating…" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openRenameDialog} onClose={() => setOpenRenameDialog(false)}>
                    <DialogTitle>Rename Service Provider</DialogTitle>
                    <DialogContent sx={{ minWidth: 350, pt: 2 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Service provider name"
                            value={renameSlotName}
                            onChange={(e) => setRenameSlotName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && renameSlotName.trim()) {
                                    handleRenameSlot();
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenRenameDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleRenameSlot}
                            disabled={renamingSlot || !renameSlotName.trim()}
                            variant="contained"
                        >
                            {renamingSlot ? "Saving…" : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openBlockDialog} onClose={() => setOpenBlockDialog(false)}>
                    <DialogTitle>Add PTO/Holiday Block</DialogTitle>
                    <DialogContent sx={{ minWidth: 350, pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Title (e.g., PTO, Holiday)"
                            value={blockData.title}
                            onChange={(e) => setBlockData({ ...blockData, title: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            label="Start"
                            type="datetime-local"
                            value={blockData.start}
                            onChange={(e) => setBlockData({ ...blockData, start: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            label="End"
                            type="datetime-local"
                            value={blockData.end}
                            onChange={(e) => setBlockData({ ...blockData, end: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenBlockDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleCreateBlock}
                            disabled={creatingBlock || !blockData.title.trim() || !blockData.start || !blockData.end}
                            variant="contained"
                        >
                            {creatingBlock ? "Creating…" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={openDeactivateDialog} onClose={() => !deactivatingSlot && setOpenDeactivateDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Deactivate Service Provider</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        {futureAppointments.length === 0 ? (
                            <Typography>This service provider has no future appointments. You can deactivate it safely.</Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant="subtitle2">
                                    This service provider has {futureAppointments.length} future appointment(s) that need to be handled:
                                </Typography>
                                {futureAppointments.map((apt) => {
                                    const validDestinationSlots = localSlots.filter(
                                        s => s.id !== selectedSlotId && s.status !== 0 && apt.validDestinationSlotIds?.includes(s.id)
                                    );
                                    const hasValidSlots = validDestinationSlots.length > 0;
                                    return (
                                        <Box key={apt.id} sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {apt.clientName || 'Unknown Client'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {dayjs(apt.start).format('MMM D, YYYY HH:mm')} ({apt.length} min)
                                            </Typography>
                                            {hasValidSlots ? (
                                                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                                    <InputLabel>Move to service provider</InputLabel>
                                                    <Select
                                                        value={appointmentMoves[apt.id] || ""}
                                                        label="Move to service provider"
                                                        onChange={(e) => setAppointmentMoves({ ...appointmentMoves, [apt.id]: e.target.value })}
                                                    >
                                                        <MenuItem value="">No service provider (manual reschedule)</MenuItem>
                                                        {validDestinationSlots.map((slot) => (
                                                            <MenuItem key={slot.id} value={slot.id}>
                                                                {slot.name || slot.id}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3cd', borderRadius: 1 }}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        No valid destination slots available. This appointment will be added to the manual reschedule queue.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDeactivateDialog(false)} disabled={deactivatingSlot}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDeactivation}
                            variant="contained"
                            color="warning"
                            disabled={deactivatingSlot}
                        >
                            {deactivatingSlot ? "Deactivating…" : "Confirm Deactivation"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {manualRescheduleQueue.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Manual Reschedule Queue
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                            Appointments that need manual rescheduling
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {manualRescheduleQueue.map((item) => (
                                <Box
                                    key={item.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 1.5,
                                        bgcolor: item.status === 'pending' ? '#fff3cd' : '#e8f5e9',
                                        border: item.status === 'pending' ? '1px solid #ffc107' : '1px solid #81c784',
                                        borderRadius: 1,
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {item.clientName || "Unknown Client"}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                            Phone: {item.clientPhone || "Not provided"}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                            Email: {item.clientEmail || "Not provided"}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                            Original appointment: {formatQueueDateTime(item.appointmentStart)} ({item.appointmentLength} min)
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                            Reason: {item.reason || "Manual reschedule needed"}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                            Status: {item.status}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        <Typography variant="caption" sx={{
                                            px: 1,
                                            py: 0.5,
                                            bgcolor: item.status === 'pending' ? '#fff8e1' : '#c8e6c9',
                                            borderRadius: '4px',
                                            textTransform: 'capitalize'
                                        }}>
                                            {item.status}
                                        </Typography>
                                        {item.status === 'pending' && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleOpenRescheduleDialog(item)}
                                                disabled={getActiveSlots().length === 0}
                                            >
                                                Reschedule
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <Dialog open={openRescheduleDialog} onClose={handleCloseRescheduleDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Reschedule Appointment</DialogTitle>
                    <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedQueueItem && (
                            <>
                                <Typography variant="body2">
                                    {selectedQueueItem.clientName || "Unknown Client"}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Original appointment: {formatQueueDateTime(selectedQueueItem.appointmentStart)} ({selectedQueueItem.appointmentLength} min)
                                </Typography>
                            </>
                        )}
                        {rescheduleError && <Alert severity="error">{rescheduleError}</Alert>}
                        <FormControl fullWidth size="small">
                            <InputLabel id="reschedule-slot-select-label">Active service provider</InputLabel>
                            <Select
                                labelId="reschedule-slot-select-label"
                                value={rescheduleForm.newSlotId}
                                label="Active service provider"
                                onChange={(e) => setRescheduleForm((current) => ({ ...current, newSlotId: e.target.value, newStart: "" }))}
                            >
                                {getActiveSlots().map((slot) => (
                                    <MenuItem key={slot.id} value={slot.id}>
                                        {slot.name ?? slot.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <DatePicker
                            label="Date"
                            value={rescheduleDate}
                            minDate={dayjs().startOf('day')}
                            onChange={(newDate) => {
                                setRescheduleDate(newDate);
                                setRescheduleForm((current) => ({ ...current, newStart: "" }));
                            }}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                        {rescheduleForm.newSlotId && rescheduleDate && (
                            <Box>
                                {availableTimesLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2">Loading available times…</Typography>
                                    </Box>
                                ) : availableTimes.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary">
                                        No available times for this slot on the selected date.
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {availableTimes.map((time) => (
                                            <Button
                                                key={time}
                                                variant={rescheduleForm.newStart === time ? "contained" : "outlined"}
                                                size="small"
                                                onClick={() => setRescheduleForm((current) => ({ ...current, newStart: time }))}
                                            >
                                                {dayjs(time).format('HH:mm')}
                                            </Button>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseRescheduleDialog} disabled={reschedulingAppointment}>Cancel</Button>
                        <Button
                            onClick={handleSubmitReschedule}
                            variant="contained"
                            disabled={reschedulingAppointment || !rescheduleForm.newSlotId || !rescheduleForm.newStart}
                        >
                            {reschedulingAppointment ? "Saving…" : "Reschedule"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoFileSelect}
                    ref={photoInputRef}
                    style={{ display: 'none' }}
                />

                {showPhotoModal && (
                    <CropPhotoModal
                        imageSrc={photoModalImageSrc}
                        setImageSrc={setPhotoModalImageSrc}
                        setShowModal={setShowPhotoModal}
                        onCropComplete={handlePhotoCropComplete}
                        aspect={1}
                        cropShape="round"
                        maxSize={512}
                        quality={0.9}
                    />
                )}
            </Box>
        </LocalizationProvider>
    );
}


